import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, getUserDisplayName } from '../utils/auth';
import { formatRepairNumber } from '../utils/format';

const API_BASE = 'https://vhnsv33dvk.execute-api.eu-west-1.amazonaws.com';

const STATUS_LABELS = {
  initial_inspection:  'Initial Inspection',
  estimate_pending:    'Estimate Pending',
  estimate_sent:       'Estimate Shared',
  estimate_approved:   'Estimate Approved',
  waiting_parts:       'Waiting Parts',
  in_progress:         'In Progress',
  on_test:             'On Test',
  completed:           'Ready for Collection',
  collected:           'Collected',
  cancelled:           'Cancelled',
  returned:            'Returned',
  return_in_progress:  'Return In Progress',
  no_repair:           'No Repair'
};

const TRANSITION_LABELS = {
  estimate_pending:    'Request Estimate',
  estimate_sent:       'Mark Estimate Shared',
  in_progress:         'Start Work',
  waiting_parts:       'Waiting for Parts',
  on_test:             'Move to Testing',
  completed:           'Mark Complete',
  collected:           'Mark Collected',
  cancelled:           'Cancel Repair',
  no_repair:           'No Repair Possible',
  returned:            'Mark Returned',
  return_in_progress:  'Start Return Work',
};

// Overrides keyed by "fromStatus->toStatus", for transitions where the
// generic TRANSITION_LABELS entry doesn't fit every path into that status.
// in_progress is reachable from four different places (see VALID_TRANSITIONS),
// and "Start Work" only makes sense for the first one.
const TRANSITION_LABEL_OVERRIDES = {
  'waiting_parts->in_progress':  'Resume Work',
  'on_test->in_progress':        'Back to In Progress',
  'completed->in_progress':      'Back to In Progress',
};

export function getTransitionLabel(fromStatus, toStatus) {
  return TRANSITION_LABEL_OVERRIDES[`${fromStatus}->${toStatus}`]
    || TRANSITION_LABELS[toStatus]
    || STATUS_LABELS[toStatus];
}

const STATUS_COLOURS = {
  initial_inspection:  '#95a5a6',
  estimate_pending:    '#e67e22',
  estimate_sent:       '#f39c12',
  estimate_approved:   '#27ae60',
  waiting_parts:       '#8e44ad',
  in_progress:         '#2980b9',
  on_test:             '#16a085',
  completed:           '#27ae60',
  collected:           '#7f8c8d',
  cancelled:           '#c0392b',
  returned:            '#e74c3c',
  return_in_progress:  '#c0392b',
  no_repair:           '#7f8c8d'
};

const VALID_TRANSITIONS = {
  initial_inspection:  ['estimate_pending', 'in_progress'],
  estimate_pending:    ['estimate_sent', 'no_repair', 'cancelled'],
  estimate_sent:       ['estimate_approved', 'cancelled'],
  estimate_approved:   ['in_progress', 'waiting_parts', 'cancelled'],
  waiting_parts:       ['in_progress'],
  in_progress:         ['waiting_parts', 'on_test'],
  on_test:             ['completed', 'in_progress'],
  completed:           ['collected', 'in_progress'],
  collected:           ['returned'],
  returned:            ['return_in_progress'],
  return_in_progress:  ['on_test', 'waiting_parts'],
  no_repair:           ['collected'],
  cancelled:           []
};

// Button display order, left-to-right: backward/exception transitions on
// the left, forward-moving transitions on the right. VALID_TRANSITIONS
// above stays untouched as the source of truth for what's valid; this
// only reorders the subset of statuses where that array's natural order
// doesn't already read left-to-right as "exception -> normal progress".
// Statuses not listed here (single transition, or already in the right
// order) fall through to VALID_TRANSITIONS unchanged.
const TRANSITION_ORDER_OVERRIDES = {
  estimate_pending:  ['no_repair', 'cancelled', 'estimate_sent'],
  estimate_sent:     ['cancelled', 'estimate_approved'],
  estimate_approved: ['cancelled', 'waiting_parts', 'in_progress'],
  on_test:           ['in_progress', 'completed'],
  completed:         ['in_progress', 'collected'],
};

export function getOrderedTransitions(fromStatus) {
  return TRANSITION_ORDER_OVERRIDES[fromStatus] || VALID_TRANSITIONS[fromStatus] || [];
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
}

function RepairDetail({ token, onLogout }) {
  const { repair_number } = useParams();
  const navigate = useNavigate();
  const displayName = getUserDisplayName(token);

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  const [repair, setRepair] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit state per section
  const [editingSection, setEditingSection] = useState(null); // 'customer' | 'item' | 'payment'
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

 // Status update
  const [statusNote, setStatusNote] = useState('');
  const [estimateAmount, setEstimateAmount] = useState('');
  const [finalAmount, setFinalAmount] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Workshop note
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Mark as called
  const [markingCalled, setMarkingCalled] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRepair();
    fetchHistory();
  }, [repair_number]);

  const fetchRepair = async () => {
    try {
      const response = await apiFetch(
        `${API_BASE}/repairs?repair_number=${repair_number}`,
        {},
        onLogout
      );
      if (!response) return;
      const data = await response.json();
      setRepair(data);

      // Pre-populate finalAmount with final_amount if set, otherwise estimate_amount
      if (data.final_amount) {
        setFinalAmount(data.final_amount.toFixed(2));
      } else if (data.estimate_amount) {
        setFinalAmount(data.estimate_amount.toFixed(2));
      }

    } catch (err) {
      setError('Failed to load repair.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await apiFetch(
        `${API_BASE}/repairs/${repair_number}/history`,
        {},
        onLogout
      );
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      // non-critical
    }
  };

  const startEditing = (section) => {
    setEditingSection(section);
    setEditValues({ ...repair });
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditValues({});
  };

  const handleEditChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSection = async () => {
    setSaving(true);
    try {
      const response = await apiFetch(
        `${API_BASE}/repairs/${repair_number}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editValues)
        },
        onLogout
      );
      if (response.ok) {
        setEditingSection(null);
        setEditValues({});
        await fetchRepair();
      }
    } catch (err) {
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
  setUpdatingStatus(true);
  try {
    // Save estimate amount first if provided, before status change triggers notification
    if (repair.status === 'estimate_pending' && estimateAmount) {
      await apiFetch(
        `${API_BASE}/repairs/${repair_number}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            estimate_amount: parseFloat(estimateAmount),
            estimate_given_at: new Date().toISOString()
          })
        },
        onLogout
      );
    }

    // Save final amount if moving to completed
    if (newStatus === 'completed' && finalAmount) {
      const final = parseFloat(finalAmount);
      const deposit = repair.deposit_amount ? parseFloat(repair.deposit_amount) : 0;
      const balance = final - deposit;

      await apiFetch(
        `${API_BASE}/repairs/${repair_number}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            final_amount: final,
            balance_amount: balance,
          })
        },
        onLogout
      );
    }

    // Then update status (which triggers notification)
    const response = await apiFetch(
      `${API_BASE}/repairs/${repair_number}/status`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          notes: (repair.status === 'estimate_pending' && estimateAmount)
            ? `Estimate of £${parseFloat(estimateAmount).toFixed(2)} given to customer${statusNote ? `. ${statusNote}` : '.'}`
            : statusNote || null,
          changed_by: 'Admin'
        })
      },
      onLogout
    );
    if (response.ok) {
      setEstimateAmount('');
      setStatusNote('');
      await fetchRepair();
      await fetchHistory();
    }
  } catch (err) {
    alert('Failed to update status.');
  } finally {
    setUpdatingStatus(false);
  }
};

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const response = await apiFetch(
        `${API_BASE}/repairs/${repair_number}/notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: newNote, added_by: 'Admin' })
        },
        onLogout
      );
      if (response.ok) {
        setNewNote('');
        await fetchHistory();
      }
    } catch (err) {
      alert('Failed to add note.');
    } finally {
      setAddingNote(false);
    }
  };

  const handleEstimateDecision = async (approved) => {
    setUpdatingStatus(true);
    try {
      await apiFetch(
        `${API_BASE}/repairs/${repair_number}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            estimate_approved: approved,
            estimate_approved_at: new Date().toISOString()
          })
        },
        onLogout
      );

      if (approved) {
        // Transition to estimate_approved status
        await apiFetch(
          `${API_BASE}/repairs/${repair_number}/status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              status: 'estimate_approved',
              notes: 'Customer approved estimate',
              changed_by: 'Admin'
            })
          },
          onLogout
        );
      } else {
        // Just add a history note for declined
        await apiFetch(
          `${API_BASE}/repairs/${repair_number}/history/note`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              notes: 'Customer declined estimate',
              changed_by: 'Admin'
            })
          },
          onLogout
        );
      }

      await fetchRepair();
      await fetchHistory();
    } catch (err) {
      alert('Failed to record estimate decision.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkCalled = async () => {
    setMarkingCalled(true);
    try {
      await apiFetch(`${API_BASE}/repairs/${repair_number}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: 'Customer called — notified item ready for collection',
          added_by: 'Admin'
        })
      }, onLogout);
      await apiFetch(`${API_BASE}/repairs/${repair_number}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collection_called_at: new Date().toISOString() })
      }, onLogout);

      await fetchRepair();
      await fetchHistory();
    } catch (err) {
      alert('Failed to mark as called.');
    } finally {
      setMarkingCalled(false);
    }
  };

  if (loading) return <div className="portal"><p className="loading-text">Loading...</p></div>;
  if (error) return <div className="portal"><p className="error-text">{error}</p></div>;
  if (!repair) return null;

  const transitions = getOrderedTransitions(repair.status);
  const isEditing = (section) => editingSection === section;

  return (
    <div className="portal">
      <nav className="portal-nav">
        <div className="portal-nav-brand">Repair Shop</div>
        <div className="portal-nav-links">
          <button className="nav-btn" onClick={() => navigate('/admin')}>Dashboard</button>
          <button className="nav-btn" onClick={() => navigate('/admin/search')}>Search</button>
          <button className="nav-btn" onClick={() => navigate('/admin/repairs/new')}>New Repair</button>
          <button className="nav-btn logout" onClick={handleLogoutClick}>{displayName || 'Logout'}</button>
        </div>
      </nav>

      <div className="portal-content">

        <div className="detail-header">
          <div className="detail-header-left">
            <button className="back-btn" onClick={() => navigate('/admin')}>← Back</button>
            <h1>Repair #{formatRepairNumber(repair.repair_number)}</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="print-btn" onClick={() => window.print()}>🖨 Print</button>
            <span className="repair-status large" style={{ background: STATUS_COLOURS[repair.status] }}>
              {STATUS_LABELS[repair.status]}
            </span>
          </div>
        </div>

        <div className="detail-grid">

          {/* Customer */}
          <div className="detail-card">
            <div className="detail-card-title">
              <h2>Customer</h2>
              {!isEditing('customer')
                ? <button className="edit-link" onClick={() => startEditing('customer')}>edit</button>
                : <div className="edit-actions">
                    <button className="save-link" onClick={handleSaveSection} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button className="cancel-link" onClick={cancelEditing}>Cancel</button>
                  </div>
              }
            </div>
            <div className="detail-rows">
              {isEditing('customer') ? (
                <>
                  {[
                    { label: 'Name', field: 'customer_name' },
                    { label: 'Phone', field: 'customer_phone' },
                    { label: 'Email', field: 'customer_email', type: 'email' },
                    { label: 'Address', field: 'customer_address1' },
                    { label: 'Address 2', field: 'customer_address2' },
                    { label: 'Town', field: 'customer_town' },
                    { label: 'Postcode', field: 'customer_postcode' },
                  ].map(({ label, field, type = 'text' }) => (
                    <div key={field} className="detail-row">
                      <span className="detail-label">{label}</span>
                      <input
                        type={type}
                        className="section-edit-input"
                        value={editValues[field] || ''}
                        onChange={(e) => handleEditChange(field, e.target.value)}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{repair.customer_name || '—'}</span></div>
                  <div className="detail-row"><span className="detail-label">Phone</span><span className="detail-value">{repair.customer_phone ? <a href={`tel:${repair.customer_phone}`}>{repair.customer_phone}</a> : '—'}</span></div>
                  <div className="detail-row"><span className="detail-label">Email</span><span className="detail-value">{repair.customer_email ? <a href={`mailto:${repair.customer_email}`}>{repair.customer_email}</a> : '—'}</span></div>
                  <div className="detail-row"><span className="detail-label">Address</span><span className="detail-value">{[repair.customer_address1, repair.customer_address2, repair.customer_town, repair.customer_postcode].filter(Boolean).join(', ') || '—'}</span></div>
                </>
              )}
            </div>
          </div>

          {/* Item */}
          <div className="detail-card">
            <div className="detail-card-title">
              <h2>Item</h2>
              {!isEditing('item')
                ? <button className="edit-link" onClick={() => startEditing('item')}>edit</button>
                : <div className="edit-actions">
                    <button className="save-link" onClick={handleSaveSection} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button className="cancel-link" onClick={cancelEditing}>Cancel</button>
                  </div>
              }
            </div>
            <div className="detail-rows">
              {isEditing('item') ? (
                <>
                  {[
                    { label: 'Type', field: 'item_type', type: 'select' },
                    { label: 'Description', field: 'item_description' },
                    { label: 'Fault Reported', field: 'fault_reported' },
                  ].map(({ label, field, type = 'text' }) => (
                    <div key={field} className="detail-row">
                      <span className="detail-label">{label}</span>
                      {type === 'select' ? (
                          <select
                              className="section-edit-input"
                              value={editValues[field] || ''}
                              onChange={(e) => handleEditChange(field, e.target.value)}
                          >
                              <option value="watch">Watch</option>
                              <option value="clock">Clock</option>
                              <option value="jewellery">Jewellery</option>
                              <option value="other">Other</option>
                          </select>
                      ) : (
                          <input
                              type={type}
                              className="section-edit-input"
                              value={editValues[field] || ''}
                              onChange={(e) => handleEditChange(field, e.target.value)}
                          />
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value" style={{ textTransform: 'capitalize' }}>{repair.item_type}</span></div>
                  <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{repair.item_description || '—'}</span></div>
                  <div className="detail-row"><span className="detail-label">Fault Reported</span><span className="detail-value">{repair.fault_reported || '—'}</span></div>
                  <div className="detail-row"><span className="detail-label">Received</span><span className="detail-value">{formatDate(repair.created_at)}</span></div>
                  {repair.completed_at && <div className="detail-row"><span className="detail-label">Completed</span><span className="detail-value">{formatDate(repair.completed_at)}</span></div>}
                </>
              )}
            </div>
          </div>
    
          {/* Payment */}
          <div className="detail-card">
            <div className="detail-card-title">
              <h2>Payment</h2>
              {!isEditing('payment')
                ? <button className="edit-link" onClick={() => startEditing('payment')}>edit</button>
                : <div className="edit-actions">
                    <button className="save-link" onClick={handleSaveSection} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button className="cancel-link" onClick={cancelEditing}>Cancel</button>
                  </div>
              }
            </div>
            <div className="detail-rows">
              {isEditing('payment') ? (
                <>
                  {[
                    { label: 'Estimate', field: 'estimate_amount' },
                    { label: 'Final Amount', field: 'final_amount' },
                    { label: 'Deposit Paid', field: 'deposit_amount' },
                    { label: 'Balance Due', field: 'balance_amount' },
                  ].map(({ label, field }) => (
                    <div key={field} className="detail-row">
                      <span className="detail-label">{label}</span>
                      <input
                        type="number"
                        step="0.01"
                        className="section-edit-input"
                        value={editValues[field] || ''}
                        onChange={(e) => handleEditChange(field, e.target.value)}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="detail-row"><span className="detail-label">Estimate</span><span className="detail-value">{formatCurrency(repair.estimate_amount)}</span></div>
                  <div className="detail-row"><span className="detail-label">Final Amount</span><span className="detail-value">{formatCurrency(repair.final_amount)}</span></div>
                  <div className="detail-row"><span className="detail-label">Deposit Paid</span><span className="detail-value">{formatCurrency(repair.deposit_amount)}</span></div>
                  <div className="detail-row"><span className="detail-label">Balance Due</span><span className="detail-value" style={{ color: repair.balance_paid ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>{repair.balance_paid ? '✓ Paid' : formatCurrency(repair.balance_amount)}</span></div>
                  <div className="detail-row feature-hidden"><span className="detail-label">Notify Email</span><span className="detail-value">{repair.notify_email ? 'Yes' : 'No'}</span></div>
                </>
              )}
            </div>
          </div>

          {/* Status transitions */}
          <div className="detail-card">
            <div className="detail-card-title"><h2>Update Status</h2></div>
            {repair.status === 'estimate_sent' ? (
              <>
                {repair.estimate_approved === null ? (
                  <>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem' }}>
                      Record customer decision:
                    </p>
                    <div className="transition-buttons">
                      <button
                        className="transition-btn"
                        style={{ background: '#27ae60' }}
                        onClick={() => handleEstimateDecision(true)}
                        disabled={updatingStatus}
                      >
                        ✓ Customer Approved
                      </button>
                      <button
                        className="transition-btn"
                        style={{ background: '#e74c3c' }}
                        onClick={() => handleEstimateDecision(false)}
                        disabled={updatingStatus}
                      >
                        ✗ Customer Declined
                      </button>
                      <button
                        className="transition-btn"
                        style={{ background: '#7f8c8d' }}
                        onClick={() => handleStatusUpdate('cancelled')}
                        disabled={updatingStatus}
                      >
                        Cancel Repair
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.8rem', borderRadius: '6px',
                      background: '#fdf0f0', border: '1px solid #e74c3c',
                      fontSize: '0.85rem', color: '#e74c3c', fontWeight: 600
                    }}>
                      ✗ Estimate declined by customer
                    </div>
                    <div className="transition-buttons">
                      <button
                        className="transition-btn"
                        style={{ background: '#7f8c8d' }}
                        onClick={() => handleStatusUpdate('cancelled')}
                        disabled={updatingStatus}
                      >
                        Cancel Repair
                      </button>
                    </div>
                  </>
                )}
              </>
                            
            ) : (
              <>
                {transitions.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '0.9rem' }}>No further transitions available.</p>
                ) : (
                  <>
                    <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                      <label>Comment (optional)</label>
                      <input
                        type="text"
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        placeholder="Enter a comment..."
                      />
                    </div>
                    {repair.status === 'estimate_pending' && (
                      <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                        <label>Estimate Amount (£)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={estimateAmount}
                          onChange={(e) => setEstimateAmount(e.target.value)}
                          placeholder={repair.estimate_amount ? parseFloat(repair.estimate_amount).toFixed(2) : '0.00'}
                        />
                      </div>
                    )}
                    {repair.status === 'on_test' && (
                      <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                        <label>Final Amount (£)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={finalAmount}
                          onChange={(e) => setFinalAmount(e.target.value)}
                          placeholder="0.00"
                        />
                        {repair.final_amount && (
                          <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.3rem' }}>
                            Currently set to £{parseFloat(repair.final_amount).toFixed(2)}
                            {repair.deposit_amount ? ` — deposit of £${parseFloat(repair.deposit_amount).toFixed(2)} already taken` : ''}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="transition-buttons">
                      {transitions.map(status => (
                        <button
                          key={status}
                          className="transition-btn"
                          style={{ background: STATUS_COLOURS[status] }}
                          onClick={() => handleStatusUpdate(status)}
                          disabled={updatingStatus}
                        >
                          {getTransitionLabel(repair.status, status)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            {(repair.status === 'completed' || repair.status === 'no_repair') && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
                 <button
                  className="transition-btn"
                  style={{ background: repair.collection_called_at ? '#7f8c8d' : '#2c3e50' }}
                  onClick={handleMarkCalled}
                  disabled={markingCalled}
                >
                  {repair.collection_called_at
                    ? '📞 Call Again'
                    : '📞 Mark as Called'}
                </button>
              </div>
            )}
          </div>

          {/* Add Note */}
          <div className="detail-card full-width">
            <div className="detail-card-title"><h2>Add Note</h2></div>
            <form onSubmit={handleAddNote}>
              <div className="form-group">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter workshop note..."
                />
              </div>
              <button type="submit" disabled={addingNote || !newNote.trim()}>
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </form>
          </div>

          {/* Timeline */}
          <div className="detail-card full-width">
            <div className="detail-card-title"><h2>Timeline</h2></div>
            {history.length === 0 ? (
              <p style={{ color: '#888', fontSize: '0.9rem' }}>No history available.</p>
            ) : (
              <div className="history-list">
                {history.map((entry, index) => (
                  <div key={index} className={`history-entry ${entry.entry_type === 'note' ? 'history-entry-note' : ''}`}>
                    <div
                      className="history-dot"
                      style={{ background: entry.entry_type === 'note' ? '#bdc3c7' : STATUS_COLOURS[entry.status] }}
                    />
                    <div className="history-content">
                      <div className="history-header">
                        {entry.entry_type === 'status' ? (
                          <span className="history-status" style={{ color: STATUS_COLOURS[entry.status] }}>
                            {STATUS_LABELS[entry.status]}
                          </span>
                        ) : (
                          <span className="history-status" style={{ color: '#888' }}>
                            📝 Note
                          </span>
                        )}
                        <span className="history-date">{formatDate(entry.changed_at)}</span>
                      </div>
                      {entry.entry_type === 'note' && (
                        <p className="history-notes">{entry.notes}</p>
                      )}
                      {entry.entry_type === 'status' && entry.notes && (
                        <p className="history-notes">{entry.notes}</p>
                      )}
                      {entry.changed_by && <p className="history-by">by {entry.changed_by}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default RepairDetail;